import { useState, useRef, useEffect } from "react";
import { Inbox, ArrowRightLeft, Send, StickyNote, CalendarClock, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";

const ICONS = { received: Inbox, status: ArrowRightLeft, reply: Send, note: StickyNote, follow_up: CalendarClock };
const COLORS = { received: "text-cyan-600", status: "text-violet-600", reply: "text-emerald-600", note: "text-amber-600", follow_up: "text-rose-600" };

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const Event = ({ ev }) => {
  const Icon = ICONS[ev.type] || StickyNote;
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`activity-event-${ev.type}`} className="relative pl-8">
      <span className={`absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white border border-slate-200 ${COLORS[ev.type] || ""}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="text-xs text-slate-500">{when(ev.at)}</div>
      {ev.type === "reply" ? (
        <div>
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-left text-sm text-slate-800 inline-flex items-center gap-1 hover:text-cyan-700">
            Reply sent: <span className="font-medium">{ev.subject}</span> <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-700 bg-white border border-slate-200 rounded-lg p-3">{ev.text}</pre>}
        </div>
      ) : (
        <p className="text-sm text-slate-800 whitespace-pre-wrap">
          {ev.text}
          {ev.type === "note" && ev.follow_up_at && <span className="ml-2 text-xs text-rose-600">· follow up {ev.follow_up_at}</span>}
        </p>
      )}
    </li>
  );
};

const NoteForm = ({ lead, onAdd }) => {
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onAdd(lead.id, text.trim(), date || null);
      setText("");
      setDate("");
      toast.success("Note added");
    } catch {
      toast.error("Couldn't save note");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-4 rounded-xl border border-slate-200 bg-white p-3" data-testid={`note-form-${lead.reference}`}>
      <Textarea
        ref={inputRef}
        data-testid={`note-text-${lead.reference}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Private note — call summary, next step, who you spoke to…"
        className="bg-white border-slate-300 text-sm"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-500" htmlFor={`follow-${lead.id}`}>Follow-up date</label>
        <Input id={`follow-${lead.id}`} data-testid={`note-date-${lead.reference}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-[160px] bg-white border-slate-300 text-sm" />
        <button
          type="submit"
          data-testid={`note-submit-${lead.reference}`}
          disabled={saving || !text.trim()}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />} Add note
        </button>
      </div>
    </form>
  );
};

export const ActivityTimeline = ({ lead, onAddNote, open, setOpen }) => {
  const events = [...(lead.activity || [])].sort((a, b) => (a.at < b.at ? 1 : -1));
  return (
    <div className="mt-4 border-t border-slate-200 pt-3">
      <button
        type="button"
        data-testid={`activity-toggle-${lead.reference}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-cyan-700"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /> Notes &amp; activity
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{events.length}</span>
      </button>
      {open && (
        <div data-testid={`activity-panel-${lead.reference}`}>
          <NoteForm lead={lead} onAdd={onAddNote} />
          <ol className="mt-4 space-y-4 border-l border-slate-200 ml-3 pl-2" data-testid={`activity-list-${lead.reference}`}>
            {events.map((ev) => <Event key={ev.id} ev={ev} />)}
            {events.length === 0 && <li className="pl-8 text-sm text-slate-500">No activity recorded yet.</li>}
          </ol>
        </div>
      )}
    </div>
  );
};
